import React, { useState } from 'react';
import { PackageItem, Operator, Wallet, getAvailableBalance } from '../../types';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/api';
import { X, Check, ArrowRight, ShieldCheck, Copy, Upload, AlertTriangle, Send } from 'lucide-react';
import { OperatorLogo, PaymentLogo } from '../common/BrandLogo';
import { compressImage } from '../../lib/image';

interface CheckoutModalProps {
  pkg: PackageItem;
  operator: Operator;
  wallet?: Wallet;
  onClose: () => void;
  onOrderSuccess: (orderId: string) => void;
}

export const CheckoutModal: React.FC<CheckoutModalProps> = ({
  pkg,
  operator,
  wallet,
  onClose,
  onOrderSuccess,
}) => {
  const { t } = useLanguage();
  const { isGuest } = useAuth();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [recipientPhone, setRecipientPhone] = useState('09');
  const [phoneConfirmed, setPhoneConfirmed] = useState(false);
  const availableBalance = getAvailableBalance(wallet);
  const hasEnoughWallet = availableBalance >= pkg.selling_price;
  const [paymentMethod, setPaymentMethod] = useState<'wallet' | 'direct_kpay'>(
    hasEnoughWallet ? 'wallet' : 'direct_kpay'
  );
  const [txRef, setTxRef] = useState('');
  const [slipUrl, setSlipUrl] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFull, setPreviewFull] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [receivingAccounts, setReceivingAccounts] = useState<any[]>([]);

  React.useEffect(() => {
    api.getReceivingAccounts().then((accs) => {
      if (accs && accs.length > 0) {
        setReceivingAccounts(accs);
      }
    }).catch(() => {});
  }, []);

  const kpayAccount = receivingAccounts.find(
    (a) => a.type === 'payment' && (a.provider.toLowerCase().includes('kpay') || a.id.includes('kpay'))
  );
  const displayNumber = kpayAccount?.account_number || '09987654321';
  const displayName = kpayAccount?.account_name || 'PluvP Shop';

  const handleCopyAccount = (acc: string) => {
    navigator.clipboard.writeText(acc);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = '';

    if (!file.type.startsWith('image/') && !file.name.match(/\.(jpg|jpeg|png|webp|heic|bmp|gif)$/i)) {
      setError('ကျေးဇူးပြု၍ ဓာတ်ပုံ သို့မဟုတ် Screenshot သာ ရွေးချယ်ပေးပါ');
      return;
    }

    // Instant local preview
    try {
      const localUrl = URL.createObjectURL(file);
      setPreviewUrl(localUrl);
    } catch {
      // ignore
    }

    setCompressing(true);
    setError('');

    try {
      const dataUrl = await compressImage(file);
      setSlipUrl(dataUrl);
    } catch {
      // fallback to raw FileReader
      try {
        const rawData = await new Promise<string>((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result as string);
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        setSlipUrl(rawData);
      } catch {
        setError('ပုံရိပ် တင်သွင်းမှု မအောင်မြင်ပါ။ နောက်တစ်ကြိမ် ကြိုးစားပေးပါ။');
        setPreviewUrl('');
      }
    } finally {
      setCompressing(false);
    }
  };

  const handleSubmitOrder = async () => {
    setError('');

    if (paymentMethod === 'direct_kpay' && !slipUrl) {
      setError('ကျေးဇူးပြု၍ ငွေလွှဲပြေစာ Screenshot ထည့်သွင်းပေးပါ (Payment screenshot is required)');
      fileInputRef.current?.click();
      return;
    }

    if (compressing) {
      setError('ပုံရိပ် ချုံ့နေဆဲဖြစ်ပါသည်။ ခေတ္တစောင့်ဆိုင်းပြီး ထပ်မံနှိပ်ပေးပါ။');
      return;
    }

    setLoading(true);

    try {
      const order = await api.createOrder({
        operatorId: pkg.operator_id,
        packageId: pkg.id,
        orderType: 'package',
        recipientPhone,
        amount: pkg.selling_price,
        paymentMethod,
        txRef: paymentMethod === 'direct_kpay' ? (txRef || 'DIRECT-KPAY') : undefined,
        slipUrl: paymentMethod === 'direct_kpay' ? slipUrl : undefined,
      });

      onOrderSuccess(order.id);
    } catch (err: any) {
      setError(err.message || 'Order failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-sm p-0 sm:p-4">
      <div className="bg-surface-card w-full max-w-md rounded-t-3xl sm:rounded-3xl border border-plum-border shadow-2xl overflow-hidden max-h-[90vh] flex flex-col animate-in slide-in-from-bottom-5">
        {/* Header */}
        <div className="px-5 py-4 border-b border-plum-border flex items-center justify-between">
          <div>
            <h2 className="font-bold text-plum-dark text-base">{t.packages} ဝယ်ယူခြင်း</h2>
            <p className="text-xs text-plum-muted">
              {step === 1 && 'အဆင့် 1 / 3 - Package အချက်အလက်'}
              {step === 2 && 'အဆင့် 2 / 3 - ဖုန်းနံပါတ် ဖြည့်ရန်'}
              {step === 3 && 'အဆင့် 3 / 3 - အော်ဒါ အတည်ပြုခြင်း (Confirm Order)'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-surface-page text-plum-muted hover:text-plum-dark transition touch-target"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto space-y-4 flex-1">
          {error && (
            <div className="p-3 bg-red-500/15 border border-red-500/30 rounded-xl text-xs text-red-400 flex items-start space-x-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* STEP 1: Package Summary */}
          {step === 1 && (
            <div className="space-y-3">
              <div className="bg-surface-page rounded-2xl p-4 border border-plum-border/60">
                <div className="flex items-center space-x-2">
                  <OperatorLogo id={pkg.operator_id} size="sm" className="rounded-lg shadow-xs" />
                  <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-md uppercase">
                    {operator.name}
                  </span>
                </div>
                <h3 className="font-bold text-plum-dark text-lg mt-2">{pkg.name}</h3>
                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-plum-border/60 text-xs">
                  <div>
                    <span className="text-plum-muted">Data Allowance:</span>
                    <p className="font-semibold text-plum-dark">{pkg.data_allowance}</p>
                  </div>
                  <div>
                    <span className="text-plum-muted">သက်တမ်း:</span>
                    <p className="font-semibold text-plum-dark">{pkg.validity_days} ရက်</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center px-1">
                <span className="text-sm text-plum-muted">ကျသင့်ငွေ:</span>
                <span className="text-xl font-bold text-primary">
                  {pkg.selling_price.toLocaleString()} Ks
                </span>
              </div>

              {isGuest ? (
                <div className="space-y-2 mt-4">
                  <a
                    href="https://t.me/pluvp_bot"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-4 rounded-xl transition shadow-xs flex items-center justify-center space-x-2 touch-target text-center text-sm"
                  >
                    <Send className="w-4 h-4" />
                    <span>Telegram Bot တွင် ဝယ်ယူမည်</span>
                  </a>
                  <p className="text-[11px] text-center text-plum-muted">
                    အော်ဒါတင်ခြင်းနှင့် ပို့ဆောင်မှု tracking ရရှိရန် Telegram Bot ဖြင့် ဝင်ရောက်ပေးပါ။
                  </p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-3 px-4 rounded-xl transition shadow-xs flex items-center justify-center space-x-2 touch-target mt-4"
                >
                  <span>ရှေ့သို့ (ဖုန်းနံပါတ်ဖြည့်မည်)</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          {/* STEP 2: Phone Input */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-plum-dark mb-1.5">
                  {t.recipientPhone} ({operator.name})
                </label>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  placeholder={t.phonePlaceholder}
                  className="w-full bg-surface-page border border-plum-border rounded-xl px-3.5 py-3 text-base text-plum-dark font-mono focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <p className="text-[11px] text-plum-muted mt-1">
                  နမူနာ: 094xxxxxxx (MPT), 097xxxxxxx (ATOM), 096xxxxxxx (MYTEL)
                </p>
              </div>

              <div className="p-3 bg-surface-accent rounded-xl border border-primary/20">
                <label className="flex items-start space-x-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={phoneConfirmed}
                    onChange={(e) => setPhoneConfirmed(e.target.checked)}
                    className="mt-1 w-4 h-4 text-primary rounded border-plum-border focus:ring-primary"
                  />
                  <span className="text-xs text-plum-dark leading-relaxed font-medium">
                    ဖုန်းနံပါတ် <code>{recipientPhone}</code> မှန်ကန်ကြောင်း အတည်ပြုပါသည်။ နံပါတ်မှားယွင်းပါက ပြန်လည်ပြင်ဆင်၍ မရနိုင်ပါ။
                  </span>
                </label>
              </div>

              <div className="flex space-x-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex-1 border border-plum-border text-plum-dark font-medium py-3 rounded-xl touch-target text-sm hover:bg-surface-page"
                >
                  နောက်သို့
                </button>
                <button
                  type="button"
                  disabled={!phoneConfirmed || recipientPhone.length < 9}
                  onClick={() => setStep(3)}
                  className="flex-1 bg-primary hover:bg-primary-dark disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition shadow-xs touch-target text-sm"
                >
                  ငွေပေးချေမည်
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Payment Selection */}
          {step === 3 && (
            <div className="space-y-4">
              <label className="block text-xs font-semibold text-plum-dark">
                {t.paymentMethod}
              </label>

              {/* Payment Method Selector */}
              <div className="space-y-2.5">
                {/* Wallet Option */}
                <div
                  onClick={() => setPaymentMethod('wallet')}
                  className={`p-3.5 rounded-2xl border cursor-pointer transition ${
                    paymentMethod === 'wallet'
                      ? 'bg-surface-card border-primary ring-2 ring-primary/20'
                      : 'bg-surface-page border-plum-border hover:border-primary/40'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2.5">
                      <input
                        type="radio"
                        name="paymentMethod"
                        checked={paymentMethod === 'wallet'}
                        onChange={() => setPaymentMethod('wallet')}
                        className="text-primary focus:ring-primary"
                      />
                      <span className="font-semibold text-sm text-plum-dark">PluvP Wallet</span>
                    </div>
                    <span className="text-xs font-bold text-primary">
                      လက်ကျန်: {availableBalance.toLocaleString()} Ks
                    </span>
                  </div>

                  {!hasEnoughWallet && (
                    <p className="text-[11px] text-status-red mt-1.5 ml-6">
                      Wallet လက်ကျန်ငွေ မလုံလောက်ပါ။ တိုက်ရိုက်ငွေလွှဲ ရွေးချယ်ပါ သို့မဟုတ် ငွေဖြည့်ပါ။
                    </p>
                  )}
                </div>

                {/* Direct KPay Option */}
                <div
                  onClick={() => setPaymentMethod('direct_kpay')}
                  className={`p-3.5 rounded-2xl border cursor-pointer transition ${
                    paymentMethod === 'direct_kpay'
                      ? 'bg-surface-card border-primary ring-2 ring-primary/20'
                      : 'bg-surface-page border-plum-border hover:border-primary/40'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="paymentMethod"
                      checked={paymentMethod === 'direct_kpay'}
                      onChange={() => setPaymentMethod('direct_kpay')}
                      className="text-primary focus:ring-primary mt-0.5"
                    />
                    <div className="flex items-center space-x-1.5 shrink-0">
                      <PaymentLogo method="kpay" size="sm" className="rounded-lg shadow-xs" />
                      <PaymentLogo method="wavepay" size="sm" className="rounded-lg shadow-xs" />
                    </div>
                    <div>
                      <span className="font-semibold text-sm text-plum-dark">
                        KBZPay / WavePay ဖြင့် တိုက်ရိုက်လွှဲမည်
                      </span>
                      <p className="text-[11px] text-plum-muted">လွှဲပြီးပါက ပြေစာ Screenshot တင်ပေးရပါမည်</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Direct Payment Details & Screenshot Upload Area */}
              {paymentMethod === 'direct_kpay' && (
                <div className="space-y-3 bg-surface-accent p-3.5 rounded-2xl border border-plum-border/60 animate-in fade-in-50 duration-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1.5">
                      <PaymentLogo method="kpay" size="xs" className="rounded-md" />
                      <span className="text-plum-muted text-xs">လက်ခံ KPay / WavePay:</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-mono font-bold text-plum-dark tracking-wide text-xs">{displayNumber}</span>
                      <button
                        type="button"
                        onClick={() => handleCopyAccount(displayNumber)}
                        className="px-2 py-0.5 rounded-lg bg-primary/20 hover:bg-primary/30 border border-primary/40 text-primary-light hover:text-white transition flex items-center space-x-1 text-xs font-semibold active:scale-95"
                        title="Copy account number"
                      >
                        {copied ? (
                          <>
                            <Check className="w-3 h-3 text-status-green" />
                            <span className="text-status-green font-bold text-[10px]">ကူးပြီး!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3 text-primary-light" />
                            <span className="text-primary-light font-semibold text-[10px]">Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Payment Slip / Screenshot Upload */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-semibold text-plum-dark">
                        ငွေလွှဲပြေစာ Screenshot (Payment Slip)
                      </label>
                      <span className="text-[10px] text-amber-400 font-bold bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded">
                        * လိုအပ်ပါသည်
                      </span>
                    </div>

                    {previewUrl || slipUrl ? (
                      /* Uploaded Preview Card */
                      <div className="rounded-2xl border border-primary/40 bg-surface-page p-3 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1.5 text-xs font-bold text-status-green">
                            <Check className="w-4 h-4 shrink-0" />
                            <span>Screenshot တင်ပြီးပါပြီ</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSlipUrl('');
                              setPreviewUrl('');
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }}
                            className="px-2 py-1 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 text-[11px] font-semibold flex items-center space-x-1 transition touch-target"
                          >
                            <X className="w-3.5 h-3.5" />
                            <span>ဖျက်မည်</span>
                          </button>
                        </div>

                        {/* Preview Image Container */}
                        <div
                          onClick={() => setPreviewFull(true)}
                          className="relative w-full h-44 rounded-xl overflow-hidden bg-black/60 border border-plum-border flex items-center justify-center cursor-pointer group"
                          title="နှိပ်၍ အကြီးကြည့်ရန်"
                        >
                          <img
                            src={previewUrl || slipUrl}
                            alt="Payment Slip Preview"
                            className="w-full h-full object-contain"
                          />
                          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-xs font-medium">
                            🔍 နှိပ်၍ အကြီးကြည့်မည်
                          </div>
                          {compressing && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-xs space-x-2">
                              <span className="animate-spin">⏳</span>
                              <span>ပုံရိပ် ချုံ့နေပါသည်...</span>
                            </div>
                          )}
                        </div>

                        {/* Re-upload button with native input overlay */}
                        <div className="relative">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            disabled={compressing}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          />
                          <button
                            type="button"
                            className="w-full py-2 rounded-xl border border-plum-border bg-surface-card hover:bg-surface-accent text-plum-dark text-xs font-medium transition flex items-center justify-center space-x-1.5 shadow-xs"
                          >
                            <Upload className="w-3.5 h-3.5 text-primary" />
                            <span>အခြားပုံ ပြောင်းလဲတင်မည်</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Clickable Upload Area with Native Input Overlay */
                      <div className="relative flex flex-col items-center justify-center border-2 border-dashed border-primary/50 hover:border-primary bg-primary/5 hover:bg-primary/10 rounded-2xl p-6 cursor-pointer transition text-center active:scale-[0.99]">
                        {/* Native File Input covering entire upload area */}
                        <input
                          ref={fileInputRef}
                          id="checkout-slip-file"
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          disabled={compressing}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                        />

                        <div className="p-3 rounded-2xl bg-primary/15 text-primary mb-2 pointer-events-none">
                          <Upload className={`w-6 h-6 ${compressing ? 'animate-bounce' : ''}`} />
                        </div>
                        <span className="text-sm font-bold text-plum-dark pointer-events-none">
                          {compressing ? 'ပုံရိပ် ချုံ့နေပါသည်...' : 'ငွေလွှဲပြေစာ Screenshot တင်ရန် နှိပ်ပါ'}
                        </span>
                        <span className="text-xs text-plum-muted mt-1 pointer-events-none">
                          KPay / WavePay ငွေလွှဲပြေစာ (JPG, PNG)
                        </span>
                        <div className="mt-3 px-3.5 py-1.5 rounded-full bg-primary/15 text-primary text-xs font-semibold pointer-events-none flex items-center space-x-1.5">
                          <span>📷</span>
                          <span>ပုံရွေးချယ်မည် (Choose Slip)</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-[11px] text-plum-muted mb-1">
                      Transaction ID (နောက်ဆုံး ၆ လုံး - စိတ်ကြိုက်):
                    </label>
                    <input
                      type="text"
                      value={txRef}
                      onChange={(e) => setTxRef(e.target.value)}
                      placeholder="ဥပမာ: 981245"
                      className="w-full bg-surface-card border border-plum-border rounded-lg px-2.5 py-1.5 text-xs text-plum-dark font-mono focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex space-x-2 pt-3">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="flex-1 border border-plum-border text-plum-dark font-medium py-3 rounded-xl touch-target text-sm hover:bg-surface-page"
                >
                  နောက်သို့
                </button>
                <button
                  type="button"
                  disabled={loading || compressing || (paymentMethod === 'wallet' && !hasEnoughWallet)}
                  onClick={handleSubmitOrder}
                  className="flex-2 bg-primary hover:bg-primary-dark disabled:opacity-50 text-white font-semibold py-3 px-4 rounded-xl transition shadow-xs touch-target text-sm flex items-center justify-center space-x-1"
                >
                  {loading ? (
                    <span>လုပ်ဆောင်နေပါသည်...</span>
                  ) : compressing ? (
                    <span>ပုံရိပ် ချုံ့နေပါသည်...</span>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4" />
                      <span>{t.confirmOrder}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full Image Preview Modal */}
      {previewFull && (previewUrl || slipUrl) && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4 animate-in fade-in"
          onClick={() => setPreviewFull(false)}
        >
          <div className="relative max-w-lg w-full max-h-[85vh] flex flex-col items-center">
            <button
              type="button"
              onClick={() => setPreviewFull(false)}
              className="absolute -top-10 right-0 text-white hover:text-red-400 p-1 font-bold flex items-center space-x-1 text-sm bg-black/40 rounded-lg px-2"
            >
              <X className="w-5 h-5" />
              <span>ပိတ်မည်</span>
            </button>
            <img
              src={previewUrl || slipUrl}
              alt="Full Slip Preview"
              className="max-h-[80vh] w-auto object-contain rounded-xl shadow-2xl border border-white/20"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};
